<script setup lang="ts">
import { computed, onBeforeMount, onMounted, onServerPrefetch, onUnmounted, ref, watch } from 'vue'

import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { useIntersectionObserver } from '@vueuse/core'
import LocationModal from '../checkout/components/LocationModal.vue'
import type { Location } from '@/types/Location'
import DefaultLayout from '@/layouts/default.vue'
import Container from '@/components/Structure/Container/Container.vue'
import Sidebar from './redesign/Sidebar.vue'
import EstablishmentCard from './redesign/Offering/EstablishmentCard.vue'
import LoadingEstablishmentCard from './redesign/Offering/LoadingEstablishmentCard.vue'
import CategorySlider from './redesign/CategorySlider/CategorySlider.vue'
import NavBarMobile from './redesign/NavBar/NavBarMobile.vue'
import BottomBar from './redesign/BottomBar.vue'
import ActiveFilters from './redesign/ActiveFilters.vue'
import { useSidebarHttp } from '@/http/sidebarHttp'
import { useEstablishmentHttp } from '@/http/establishmentHttp'
import { MarketplaceEstablishment } from '@/http/models/MarketplaceEstablishment'
// import { useGa, ViewType } from '@/composables/useGa'
import { Distribution } from '@/enums/Distribution'
import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'
import { getCountry } from '@/router/utils/getCountry'
import { useSessionStore } from '@/stores/useSessionStore'
import Card from '@/components/partials/Card.vue'
import type { MarketplaceSortingKey } from '@/enums/MarketplaceSortingKey'
import Distributions from '@/config/Distributions'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'
import { storeToRefs } from 'pinia'
import { useMarketingPageStore } from '@/stores/ssr/useMarketingPageStore'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const sessionStore = useSessionStore()
const marketplaceStore = useMarketplaceStore()

const { setBreadcrumbs } = useBreadcrumbStore()
const { localizedRoute } = useLocalizedRouter()
const { city } = storeToRefs(useMarketingPageStore())

if (city.value !== null) {
  setBreadcrumbs([
    {
      route: localizedRoute({ name: 'all-cities' }),
      name: t('cities.all'),
    },
    {
      route: localizedRoute({
        name: 'slug',
        params: {
          slug: city.value.slug,
        },
      }),
      name: city.value.name,
    },
  ])
} else {
  setBreadcrumbs([
    {
      name: t('nav.marketplace'),
      route: localizedRoute({ name: 'marketplace' }),
    },
  ])
}

const showLocationModal = ref<boolean>(false)
const locationModalClosable = ref<boolean>(true)

const content = ref<HTMLElement | null>(null)

const { selectedFiltersHash: filtersHash } = storeToRefs(marketplaceStore)
const { distribution } = storeToRefs(sessionStore)

const isSlugPage = computed(() => (route.name as string).endsWith('slug'))
const isMarketplace = computed(() => (route.name as string).endsWith('marketplace'))

const establishments = computed<MarketplaceEstablishment[] | null>(() => {
  if (marketplaceStore.items === null) {
    return null
  }

  return marketplaceStore.items.map((item: any) => new MarketplaceEstablishment(item))
})

const loadingEstablishmentCount = ref<number>(16)

const apiEstablishmentsQueryParams = computed(() => ({
  sort: marketplaceStore.sortingKey,
  categories: marketplaceStore.filteredCategories,
}))

onBeforeRouteLeave((to, from) => {
  if ((to.name as string).endsWith('establishment') && (from.name as string).endsWith('marketplace')) {
    marketplaceStore.setScrollPosition(window.scrollY)
  } else {
    marketplaceStore.setCurrentPage(1)
  }
})

function scrollToTop() {
  if (content.value !== null) {
    window.scrollTo({ top: content.value.offsetTop, behavior: 'smooth' })
  }

  const sidebar = document.getElementById('sidebar')

  if (sidebar) {
    sidebar.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const appEstablishmentsQueryString = computed(() => {
  return {
    q: marketplaceStore.searchQuery ?? undefined,
    distribution: sessionStore.distribution,
    sort: marketplaceStore.sortingKey,
    'categories[]': marketplaceStore.filteredCategories,
  }
})

const updateUrlFromStore = () => {
  const query = {
    // Take into account params sent to page by for Google Ads and Facebook Ads, they need this for tracking purposes (on first pageview).
    // ...route.query,
    ...appEstablishmentsQueryString.value,
  }

  const newUrl = {
    name: route.name,
    params: route.params,
    query,
  }

  const url = router.resolve(newUrl)

  router.replace(url)
}

const updateStoreFromUrl = async () => {
  const dist = route.query.distribution as string

  if (dist) {
    sessionStore.setDistribution(
        [ Distributions.TakeAway, Distributions.Delivery ].includes(parseInt(dist))
            ? parseInt(dist)
            : Distributions.TakeAway,
    )
  }

  if (route.query.sort) {
    marketplaceStore.setSortingKey(route.query.sort as MarketplaceSortingKey)
  }

  if (route.query.categories) {
    if (Array.isArray(route.query.categories)) {
      marketplaceStore.setFilteredCategories(
          (route.query.categories as string[]).map(category => parseInt(category)),
      )
    }
  }

  marketplaceStore.setSearchQuery(route.query.q === undefined ? null : route.query.q)

  updateUrlFromStore()
}

let dontUpdate = true

watch(filtersHash, async () => {
  if (dontUpdate) {
    return
  }

  if ((route.name as string).endsWith('marketplace')) {
    updateUrlFromStore()
  }

  scrollToTop()

  currentPage.value = 1
  marketplaceStore.setCurrentPage(1)

  await initialize()
})

// @ts-ignore
let searchHead

watch(() => route.query, (query) => {
  if (query.q !== undefined) {
    searchHead = useHead({
      title: `Eten zoeken op "${query.q}"`,
    })
  } else {
    // @ts-ignore
    searchHead?.dispose()
  }
})

if (isMarketplace.value) {
  useHead({
    title: t('pages.marketplace.meta_title_na'),
    meta: [
      {
        name: 'robots',
        content: 'noindex',
      },
    ],
  })
}

const establishmentsHttp = useEstablishmentHttp()

const totalItems = ref(0)
const currentPage = ref(1)

const hideHeader = ref<boolean>(false)

function handleScroll() {
  hideHeader.value = window.scrollY > (14 * parseFloat(getComputedStyle(document.documentElement).fontSize))
}

function openLocationModal(closable = true) {
  locationModalClosable.value = closable
  showLocationModal.value = true
}

// const {trackViewEstablishmentListScroll, trackViewEstablishmentList} = useGa()

async function restaurants(perPage: number = 16, appending = false) {
  const location = sessionStore.location

  if (location === null) {
    openLocationModal(false)

    return
  }

  const { lat, lng } = location.coordinates

  const marketplaceStore = useMarketplaceStore()

  const { data, meta } = await establishmentsHttp.establishments({
    distribution: distribution.value,
    q: marketplaceStore.searchQuery,
    sort: marketplaceStore.sortingKey,
    categories: marketplaceStore.filteredCategories,
    lat,
    lng,
    per_page: perPage,
    page: currentPage.value,
  })

  totalItems.value = meta.total

  if (appending) {
    marketplaceStore.setItems([ ...marketplaceStore.items, ...data ])

    // trackViewEstablishmentListScroll(data, store.state.marketplace.items.length, totalItems.value, currentPage.value)
  } else {
    marketplaceStore.setItems(data)

    // trackViewEstablishmentList(ViewType.Marketplace, data, totalItems.value)
  }

  if (perPage > 16) {
    currentPage.value = marketplaceStore.currentPage
  } else {
    currentPage.value++
  }

  marketplaceStore.setCurrentPage(currentPage.value)
}

const loadMore = ref<HTMLElement | null>(null)

const { resume } = useIntersectionObserver(
    loadMore,
    async ([ { isIntersecting } ]) => {
      if (isIntersecting && (marketplaceStore.items !== null && marketplaceStore.items.length < totalItems.value)) {
        await restaurants(16, true)
      }
    }, {
      immediate: false,
    },
)

const loadCity = async () => {
  marketplaceStore.setItems(
      await establishmentsHttp.establishmentsByCityAndCountry(city.value.slug, getCountry().value, marketplaceStore.filteredCategories),
  )
}

const { sidebarInfo } = useSidebarHttp()

async function initialize() {
  if ((route.name as string).endsWith('marketplace') && !import.meta.env.SSR) {
    if (marketplaceStore.currentPage > 1) {
      loadingEstablishmentCount.value = marketplaceStore.currentPage * 16
    } else {
      loadingEstablishmentCount.value = 16
    }

    await restaurants(marketplaceStore.currentPage > currentPage.value ? 16 * (marketplaceStore.currentPage - 1) : 16)
  } else {
    await loadCity()
  }
}

onMounted(async () => {
  window.addEventListener('scroll', handleScroll)

  if (sessionStore.distribution === null) {
    sessionStore.setDistribution(Distribution.TakeAway)
  }

  if ((route.name as string).endsWith('marketplace')) {
    await updateStoreFromUrl()
  }

  const sidebarData = await sidebarInfo()

  marketplaceStore.setCategories(sidebarData.categories)

  await initialize()

  if ((route.name as string).endsWith('marketplace')) {
    resume()
  }

  dontUpdate = false

  // if ((route.name as string).endsWith('slug') && items.value !== null) {
  //   trackViewEstablishmentList(ViewType.City, items.value, items.value.length)
  // }
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

onServerPrefetch(async () => {
  const sidebarData = await sidebarInfo()

  marketplaceStore.setCategories(sidebarData.categories)

  if ((route.name as string).endsWith('slug')) {
    await initialize()
  }
})

onBeforeMount(async () => {
  if ((route.name as string).endsWith('marketplace')) {
    marketplaceStore.setItems(null)
  }
})

async function updateAddress(location: Location) {
  sessionStore.setLocation(location)

  locationModalClosable.value = true
  showLocationModal.value = false
}
</script>

<template>
  <LocationModal v-model="showLocationModal" @address="updateAddress" :closable="locationModalClosable"/>

  <DefaultLayout>
    <div class="marketplace-redesign">
      <NavBarMobile @open-location-modal="openLocationModal"/>
      <BottomBar/>

      <div class="marketplace-header-visual"></div>

      <Container fluid>
        <Sidebar :class="{ 'sidebar-sticky': hideHeader }" @open-location-modal="openLocationModal"/>

        <div ref="content" class="marketplace-redesign__content">
          <CategorySlider/>

          <div class="city-page-info" v-if="isSlugPage">
            <h1>
              {{ city.content.title }}
            </h1>
            <div>
              {{ city.content.subtitle }}
            </div>
          </div>

          <div class="marketplace-redesign__grid-and-search-query">
            <ActiveFilters data-test-id="active-filters-on-page"/>

            <template v-if="establishments !== null">
              <div class="offering-grid" data-test-id="offering-grid" v-if="establishments.length > 0">
                <EstablishmentCard v-for="establishment in establishments"
                                   :key="establishment.id"
                                   :establishment="establishment"
                                   data-test-id="establishment-card"/>
              </div>
              <Card v-else class="no-results" data-test-id="no-establishments-found">
                <img class="no-results__img"
                     src="/assets/img/marketplacepage/unicorn.png"
                     :alt="t('images.unicorn_with_icecone')"/>
                <div>
                  <h3>{{ t('marketplace_search.no_results_found') }}</h3>
                  <p>{{ t('marketplace_search.remove_some_filters') }}</p>
                  <ActiveFilters simple class="no-results__pills"/>
                </div>
              </Card>
            </template>
            <div class="offering-grid" data-test-id="offering-grid-no-items" v-else>
              <LoadingEstablishmentCard v-for="n in loadingEstablishmentCount" :key="n"/>
            </div>
            <div ref="loadMore" class="load-more"></div>
          </div>
          <template v-if="isSlugPage">
            <div class="marketplace-slug-content" v-html="city.content.content"></div>
          </template>
        </div>
      </Container>
    </div>
  </DefaultLayout>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";

.marketplace-redesign {
  $self:            &;
  display:          block;
  background-color: #ffffff;
  padding:          0;

  @include lg-up {
    padding: 14rem 0;
  }

  &:deep(.container) {
    position: relative;
    z-index:  2;
    display:  flex;
    gap:      4rem;
    padding:  0;

    @include lg-up {
      padding-right: 4rem;
      padding-left:  4rem;
    }
  }

  &__content--sticky {
    @include lg-up {
      margin-left:  18rem;
      padding-left: 4rem;
    }
  }

  &__content {
    width:          100%;
    display:        flex;
    flex-direction: column;

    @include lg-up {
      gap:       3rem;
      min-width: 0;
    }
  }

  &__grid-and-search-query {
    position:       relative;
    display:        flex;
    flex-direction: column;
    gap:            1rem;
  }

  .no-results {
    display:     flex;
    gap:         2rem;
    align-items: center;
    padding:     2rem;

    &__pills {
      margin-top: 1rem;
    }

    &__img {
      margin-top: -1rem;
    }
  }
}

.marketplace-header-visual {
  display: none;

  @include lg-up {
    display:             block;
    position:            absolute;
    background-image:    url("/assets/img/marketplacepage/background-shape.svg");
    background-size:     contain;
    background-repeat:   no-repeat;
    background-position: top right;
    top:                 -10rem;
    right:               -40rem;
    width:               75rem;
    height:              75rem;
  }

  @include xl-up {
    top:    -10rem;
    right:  -35rem;
    width:  85rem;
    height: 85rem;
  }

  @include xxl-up {
    top:    0;
    right:  0;
    width:  calc(100vw - 34rem);
    height: 90vh;
  }
}

@mixin flex-columns {
  display:        flex;
  flex-direction: column;
  gap:            1rem;
}

@mixin grid($amount, $gap: 2rem) {
  display:               inline-grid;
  grid-template-columns: repeat($amount, calc(100% / $amount - $gap + $gap / $amount));
  gap:                   $gap;
}

.offering-grid {
  width:   100%;
  padding: 0 1rem;
  @include flex-columns;

  @include md-up {
    @include grid(2, 1rem);
  }

  @include lg-up {
    @include flex-columns;
    padding: 0;
  }

  @include xl-up {
    @include grid(2, 1rem);
  }

  @media(min-width: 1550px) {
    @include grid(3);
  }

  @include xxxl-up {
    @include grid(4);
  }
}

.sidebar-sticky {
  @include lg-up {
    padding-top: 2rem;
    max-height:  calc(100vh - 22rem); // max-height: 100vh;
  }
}

.load-more {
  position:       absolute;
  height:         800px;
  bottom:         -500px;
  width:          100%;
  pointer-events: none;
}

.marketplace-slug-content {
  :deep(h3) {
    padding: 1rem 0;
    margin:  0;
  }
}

.city-page-info {
  gap:            1rem;
  display:        flex;
  flex-direction: column;

  h1 {
    font-size: 1.2rem;
  }
}
</style>
